import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { TileEmpty } from '../TileWrapper';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = {
  count: 5,
  items: [
    { id: '1', title: 'Update fire evacuation plan', due_date: new Date(Date.now() - 604800000).toISOString().split('T')[0], daysOverdue: 7, project_id: '1', projects: { name: 'Site Alpha' } },
    { id: '2', title: 'Complete PAT testing', due_date: new Date(Date.now() - 432000000).toISOString().split('T')[0], daysOverdue: 5, project_id: '2', projects: { name: 'Main Office' } },
    { id: '3', title: 'Review first aid supplies', due_date: new Date(Date.now() - 259200000).toISOString().split('T')[0], daysOverdue: 3, project_id: '1', projects: { name: 'Site Alpha' } },
  ],
};

export function OverdueTasksTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const today = new Date().toISOString().split('T')[0];

        let query = supabase
          .from('tasks')
          .select(`
            id,
            title,
            due_date,
            project_id,
            projects!inner(name, company_id)
          `, { count: 'exact' })
          .eq('projects.company_id', company.id)
          .neq('status', 'done')
          .lt('due_date', today)
          .order('due_date', { ascending: true })
          .limit(size === 'medium' ? 5 : 3);

        if (filter?.spaceId) {
          query = query.eq('project_id', filter.spaceId);
        }

        const { data: tasks, count, error } = await query;

        if (error) throw error;

        const items = (tasks || []).map(t => ({
          ...t,
          daysOverdue: Math.ceil((new Date() - new Date(t.due_date)) / (1000 * 60 * 60 * 24)),
        }));

        setData({ count: count || 0, items });
      } catch (err) {
        console.error('Failed to load overdue tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, filter?.spaceId, size, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-center">
          <div className="w-16 h-16 rounded-full bg-gray-200 mx-auto mb-2" />
          <div className="h-4 w-20 bg-gray-200 rounded mx-auto" />
        </div>
      </div>
    );
  }

  if (data.count === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <div className="bg-green-100 p-3 rounded-full mb-2">
          <CheckCircle className="h-6 w-6 text-green-600" />
        </div>
        <p className="text-sm font-medium text-green-700">All Caught Up</p>
        <p className="text-xs text-gray-500">No overdue tasks</p>
      </div>
    );
  }

  if (size === 'small') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <div className="bg-red-100 p-3 rounded-full mb-2">
          <AlertTriangle className="h-6 w-6 text-red-600" />
        </div>
        <p className="text-3xl font-bold text-red-600">{data.count}</p>
        <p className="text-sm text-gray-600">Overdue</p>
        <p className="text-xs text-gray-400 mt-1">
          Oldest: {data.items[0]?.daysOverdue}d ago
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <Badge variant="destructive" className="text-lg px-3 py-1">
          {data.count} Overdue
        </Badge>
      </div>

      {data.items.map((task) => (
        <div
          key={task.id}
          onClick={() => navigate(`/space/${task.project_id}/tasks`)}
          className="flex items-center justify-between p-2 rounded-lg hover:bg-red-50 cursor-pointer transition-colors border border-red-100"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{task.title}</p>
            <p className="text-xs text-gray-500 truncate">{task.projects?.name}</p>
          </div>
          <span className="text-xs text-red-600 font-medium shrink-0">
            {task.daysOverdue}d overdue
          </span>
        </div>
      ))}

      {data.count > data.items.length && (
        <button
          onClick={() => navigate('/spaces')}
          className="w-full text-center text-sm text-red-600 hover:text-red-700 py-2"
        >
          +{data.count - data.items.length} more
        </button>
      )}
    </div>
  );
}
